// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../api/AxiosClient';

// Type Definitions
export interface OpenJob {
  job_id: string;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: string;
}

export interface OpenJobsResponse {
  jobs: OpenJob[];
}

export interface CreateJobPayload {
  title: string;
  department: string;
  location: string;
  employment_type?: string;
  openings?: number;
  priority?: string;
  hiring_manager?: string;
  business_unit?: string;
  target_start_date?: string;
  role_context?: string;
  job_description?: string;
  job_description_file?: File;
}

export interface AddCandidatePayload {
  job_id: string;
  name?: string;
  email?: string;
  phone?: string;
  current_title?: string;
  location?: string;
  experience_years?: number;
  experience_range?: string;
  resume_summary?: string;
  skills?: string[];
  education?: string[];
  interview_context?: string;
  cv_file?: File;
}

export interface JobCreationResponse {
  job_id: string;
  title: string;
  status: string;
  message: string;
}

export interface CandidateCreationResponse {
  candidate_id: string;
  name: string;
  email: string;
  status: string;
  screening_run?: {
    status: 'queued' | 'processing' | 'completed' | 'failed';
    run_id: string;
  };
  message: string;
}

// Parse Candidates Types
export interface ParsedCandidate {
  name: string;
  email: string;
  phone: string;
  current_title: string;
  location: string;
  experience_years: number;
  skills: string[];
  resume_summary: string;
}

export interface DuplicateInfo {
  flagged: boolean;
  match_type: 'none' | 'same_batch_email' | 'existing_candidate';
  matched_draft_id?: string;
  message?: string;
}

export interface AIGeneratedContent {
  flagged: boolean;
  likelihood: 'low' | 'medium' | 'high';
  reasons: string[];
  status?: 'checked' | 'failed' | 'not_checked';
  score?: number;
}

export interface CandidateDraft {
  draft_id: string;
  file_name: string;
  content_type: string;
  status: 'processed' | 'needs_review' | 'processing' | 'failed';
  candidate: ParsedCandidate;
  missing_mandatory_fields: string[];
  ai_generated_content: AIGeneratedContent;
  duplicate_candidate: DuplicateInfo;
  parser_status: string;
}

export interface ParseCandidatesResponse {
  success: boolean;
  parse_batch_id: string;
  job_id: string;
  created_at: string;
  expires_at: string;
  drafts: CandidateDraft[];
  max_files: number;
  remaining_slots: number;
}

// Confirm Candidates Types
export interface ConfirmCandidatePayload {
  draft_id: string;
  action: 'confirm' | 'confirm_separate' | 'remove';
  candidate?: {
    name: string;
    email: string;
    phone: string;
    current_title: string;
    location: string;
    experience_years: number;
    skills: string[];
    resume_summary: string;
    interview_context: string;
  };
}

export interface ConfirmCandidatesRequest {
  parse_batch_id: string;
  candidates: ConfirmCandidatePayload[];
}

export interface ConfirmCandidatesResponse {
  success: boolean;
  parse_batch_id: string;
  job_id: string;
  created_count: number;
  removed_count: number;
  failed_count: number;
  candidates: any[];
  removed_draft_ids: string[];
  errors: any[];
}

// Async Thunk to fetch open jobs
export const fetchOpenJobs = createAsyncThunk('jobManagement/fetchOpenJobs', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosClient.get<OpenJobsResponse>('/interview-host/jobs?status=Open');
    return response.data;
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch open jobs. Please try again.';
    return rejectWithValue(errorMessage);
  }
});

// Async Thunk to create a new job
export const createJob = createAsyncThunk(
  'jobManagement/createJob',
  async (payload: CreateJobPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Required fields
      formData.append('title', payload.title);
      formData.append('department', payload.department);
      formData.append('location', payload.location);

      // Optional fields
      if (payload.employment_type) {
        formData.append('employment_type', payload.employment_type);
      }
      if (payload.openings !== undefined) {
        formData.append('openings', payload.openings.toString());
      }
      if (payload.priority) {
        formData.append('priority', payload.priority);
      }
      if (payload.hiring_manager) {
        formData.append('hiring_manager', payload.hiring_manager);
      }
      if (payload.business_unit) {
        formData.append('business_unit', payload.business_unit);
      }
      if (payload.target_start_date) {
        formData.append('target_start_date', payload.target_start_date);
      }
      if (payload.role_context) {
        formData.append('role_context', payload.role_context);
      }

      // Job description - either text or file
      if (payload.job_description_file) {
        formData.append('job_description_file', payload.job_description_file);
      } else if (payload.job_description) {
        formData.append('job_description', payload.job_description);
      }

      const response = await axiosClient.post<JobCreationResponse>('/interview-host/jobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to create job. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk to add a candidate to a job
export const addCandidate = createAsyncThunk(
  'jobManagement/addCandidate',
  async (payload: AddCandidatePayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Optional fields (name and email now optional when CV is provided)
      if (payload.name) {
        formData.append('name', payload.name);
      }
      if (payload.email) {
        formData.append('email', payload.email);
      }
      if (payload.phone) {
        formData.append('phone', payload.phone);
      }
      if (payload.current_title) {
        formData.append('current_title', payload.current_title);
      }
      if (payload.location) {
        formData.append('location', payload.location);
      }
      if (payload.experience_years !== undefined) {
        formData.append('experience_years', payload.experience_years.toString());
      }
      if (payload.experience_range) {
        formData.append('experience_range', payload.experience_range);
      }
      if (payload.resume_summary) {
        formData.append('resume_summary', payload.resume_summary);
      }
      if (payload.skills && payload.skills.length > 0) {
        formData.append('skills', JSON.stringify(payload.skills));
      }
      if (payload.education && payload.education.length > 0) {
        formData.append('education', JSON.stringify(payload.education));
      }
      if (payload.interview_context) {
        formData.append('interview_context', payload.interview_context);
      }

      // CV file - either file or manual profile details
      if (payload.cv_file) {
        formData.append('cv_file', payload.cv_file);
      }

      const response = await axiosClient.post<CandidateCreationResponse>(
        `/interview-host/jobs/${payload.job_id}/candidates`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to add candidate. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk to bulk upload candidates via CVs
export const bulkUploadCandidates = createAsyncThunk(
  'jobManagement/bulkUploadCandidates',
  async (payload: { job_id: string; cv_files: File[] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Append all CV files
      payload.cv_files.forEach((file) => {
        formData.append('cv_files', file);
      });

      const response = await axiosClient.post(`/interview-host/jobs/${payload.job_id}/candidates/bulk`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to bulk upload candidates. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk to parse candidates (Step 1: Upload CVs and parse)
export const parseCandidates = createAsyncThunk(
  'jobManagement/parseCandidates',
  async (payload: { job_id: string; cv_files: File[] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Append all CV files (up to 20)
      payload.cv_files.forEach((file) => {
        formData.append('cv_files', file);
      });

      const response = await axiosClient.post<ParseCandidatesResponse>(
        `/interview-host/jobs/${payload.job_id}/candidates/parse`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to parse candidates. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk to confirm candidates (Step 2: Confirm parsed candidates)
export const confirmCandidates = createAsyncThunk(
  'jobManagement/confirmCandidates',
  async (payload: { job_id: string; request: ConfirmCandidatesRequest }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<ConfirmCandidatesResponse>(
        `/interview-host/jobs/${payload.job_id}/candidates/confirm`,
        payload.request
      );

      return response.data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to confirm candidates. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

interface JobManagementState {
  openJobs: OpenJob[];
  openJobsLoading: boolean;
  openJobsError: string | null;
  createJobLoading: boolean;
  createJobError: string | null;
  createJobSuccess: JobCreationResponse | null;
  addCandidateLoading: boolean;
  addCandidateError: string | null;
  addCandidateSuccess: CandidateCreationResponse | null;
  bulkUploadLoading: boolean;
  bulkUploadError: string | null;
  bulkUploadSuccess: any | null;
  parseCandidatesLoading: boolean;
  parseCandidatesError: string | null;
  parseCandidatesSuccess: ParseCandidatesResponse | null;
  confirmCandidatesLoading: boolean;
  confirmCandidatesError: string | null;
  confirmCandidatesSuccess: ConfirmCandidatesResponse | null;
}

const initialState: JobManagementState = {
  openJobs: [],
  openJobsLoading: false,
  openJobsError: null,
  createJobLoading: false,
  createJobError: null,
  createJobSuccess: null,
  addCandidateLoading: false,
  addCandidateError: null,
  addCandidateSuccess: null,
  bulkUploadLoading: false,
  bulkUploadError: null,
  bulkUploadSuccess: null,
  parseCandidatesLoading: false,
  parseCandidatesError: null,
  parseCandidatesSuccess: null,
  confirmCandidatesLoading: false,
  confirmCandidatesError: null,
  confirmCandidatesSuccess: null
};

const jobManagementSlice = createSlice({
  name: 'jobManagement',
  initialState,
  reducers: {
    clearCreateJobState: (state) => {
      state.createJobLoading = false;
      state.createJobError = null;
      state.createJobSuccess = null;
    },
    clearAddCandidateState: (state) => {
      state.addCandidateLoading = false;
      state.addCandidateError = null;
      state.addCandidateSuccess = null;
    },
    clearBulkUploadState: (state) => {
      state.bulkUploadLoading = false;
      state.bulkUploadError = null;
      state.bulkUploadSuccess = null;
    },
    clearParseCandidatesState: (state) => {
      state.parseCandidatesLoading = false;
      state.parseCandidatesError = null;
      state.parseCandidatesSuccess = null;
    },
    clearConfirmCandidatesState: (state) => {
      state.confirmCandidatesLoading = false;
      state.confirmCandidatesError = null;
      state.confirmCandidatesSuccess = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Open Jobs
      .addCase(fetchOpenJobs.pending, (state) => {
        state.openJobsLoading = true;
        state.openJobsError = null;
      })
      .addCase(fetchOpenJobs.fulfilled, (state, action) => {
        state.openJobsLoading = false;
        state.openJobs = action.payload.jobs;
        state.openJobsError = null;
      })
      .addCase(fetchOpenJobs.rejected, (state, action) => {
        state.openJobsLoading = false;
        state.openJobsError = action.payload as string;
      })
      // Create Job
      .addCase(createJob.pending, (state) => {
        state.createJobLoading = true;
        state.createJobError = null;
        state.createJobSuccess = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.createJobLoading = false;
        state.createJobSuccess = action.payload;
        state.createJobError = null;
      })
      .addCase(createJob.rejected, (state, action) => {
        state.createJobLoading = false;
        state.createJobError = action.payload as string;
        state.createJobSuccess = null;
      })
      // Add Candidate
      .addCase(addCandidate.pending, (state) => {
        state.addCandidateLoading = true;
        state.addCandidateError = null;
        state.addCandidateSuccess = null;
      })
      .addCase(addCandidate.fulfilled, (state, action) => {
        state.addCandidateLoading = false;
        state.addCandidateSuccess = action.payload;
        state.addCandidateError = null;
      })
      .addCase(addCandidate.rejected, (state, action) => {
        state.addCandidateLoading = false;
        state.addCandidateError = action.payload as string;
        state.addCandidateSuccess = null;
      })
      // Bulk Upload Candidates
      .addCase(bulkUploadCandidates.pending, (state) => {
        state.bulkUploadLoading = true;
        state.bulkUploadError = null;
        state.bulkUploadSuccess = null;
      })
      .addCase(bulkUploadCandidates.fulfilled, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadSuccess = action.payload;
        state.bulkUploadError = null;
      })
      .addCase(bulkUploadCandidates.rejected, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadError = action.payload as string;
        state.bulkUploadSuccess = null;
      })
      // Parse Candidates
      .addCase(parseCandidates.pending, (state) => {
        state.parseCandidatesLoading = true;
        state.parseCandidatesError = null;
        state.parseCandidatesSuccess = null;
      })
      .addCase(parseCandidates.fulfilled, (state, action) => {
        state.parseCandidatesLoading = false;
        state.parseCandidatesSuccess = action.payload;
        state.parseCandidatesError = null;
      })
      .addCase(parseCandidates.rejected, (state, action) => {
        state.parseCandidatesLoading = false;
        state.parseCandidatesError = action.payload as string;
        state.parseCandidatesSuccess = null;
      })
      // Confirm Candidates
      .addCase(confirmCandidates.pending, (state) => {
        state.confirmCandidatesLoading = true;
        state.confirmCandidatesError = null;
        state.confirmCandidatesSuccess = null;
      })
      .addCase(confirmCandidates.fulfilled, (state, action) => {
        state.confirmCandidatesLoading = false;
        state.confirmCandidatesSuccess = action.payload;
        state.confirmCandidatesError = null;
      })
      .addCase(confirmCandidates.rejected, (state, action) => {
        state.confirmCandidatesLoading = false;
        state.confirmCandidatesError = action.payload as string;
        state.confirmCandidatesSuccess = null;
      });
  }
});

export const {
  clearCreateJobState,
  clearAddCandidateState,
  clearBulkUploadState,
  clearParseCandidatesState,
  clearConfirmCandidatesState
} = jobManagementSlice.actions;
export default jobManagementSlice.reducer;
